import React, { FC } from 'react';
import styled from 'styled-components';
import { translations } from 'i18n';

import AppModel from 'models/app';

import { Link } from 'components/Common';
import Icon from 'components/v2/Icon';
import ImageContainer from 'components/v2/ImageContainer/index';
import { fitbitLightGray, fitbitTextBlack, fontRegular } from '../../../themes';
import StateManagementButton from '../StateManagementButton/index';

import arrowRight from 'icons/arrow-right-v2.svg';

interface AppCardProps {
  app: AppModel;
}

const AppContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  height: 64px;
  padding-right: 16px;
  padding-left: 16px;

  border-bottom: 1px solid ${({ theme }) => theme.borderColorV2};
`;

const LinkFullWidth = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const UpdateButton = styled.div`
  background-color: ${fitbitLightGray};
  color: ${fitbitTextBlack};
  text-transform: uppercase;
  padding: 5px 15px;
  border-radius: 15px;
  font-family: ${fontRegular};
`;

const CenterContent = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-around;
  height: 44px;
  padding: 0 16px;
  word-break: break-word;
`;

const RightContent = styled.div`
  padding: 0 5px;
`;

const AppIcon = styled(ImageContainer)`
  width: 32px;
  height: 32px;
`;

const Title = styled.span`
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -0.32px;

  color: #112530;
`;

const PermissionsWarning = styled.span`
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.15px;

  color: #b7832f;
`;

const App: FC<AppCardProps> = ({
  app
}) => {
  const {
    page: {
      App: { InstallButtonState },
      AppList: {
        Digest: { appIconAltText }
      },
      MyApps: { needsPermissions }
    }
  } = translations;

  const installTexts = Object.keys(InstallButtonState).reduce((texts: any, key) => {
    texts[key] = InstallButtonState[key]();
    return texts;
  }, {});

  return (
    <div>
      <AppContainer>
        <LinkFullWidth
          to={`/v2/app/${app.id}`}
          pageDirection='FORWARD'
          id={`app-${app.id}`}
          aria-label={app.name}
        >
          <AppIcon
            containerAttr={{
              iconPlaceholder: true,
              width: app.icon.width,
              height: app.icon.height,
              styles: null
            }}
            imageAttr={{
              alt: appIconAltText(),
              src: app.icon.uri,
              styles: null
            }}
          />
          <CenterContent>
            <Title>
              {app.name}
            </Title>
            {app.hasPermissionWarning && (
              <PermissionsWarning>
                {needsPermissions()}
              </PermissionsWarning>
            )}
          </CenterContent>
        </LinkFullWidth>
        {(app.hasUpdateAvailable || app.isInstalling) && (
          <RightContent>
            <StateManagementButton CustomButton={UpdateButton} app={app} texts={installTexts} />
          </RightContent>
        )}
        <Link
          to={`/v2/app/${app.id}`}
          pageDirection='FORWARD'
        >
          <Icon url={arrowRight} width={20} height={20}/>
        </Link>
      </AppContainer>
    </div>
  );
}


export default App;